<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Label-based gating shared by the CSAT and NPS surveys. The wording lives under
// INBOX_MGMT.CSAT.SURVEY_RULE for both, so the two surveys stay in one voice.
const props = defineProps({
  operator: { type: String, default: 'contains' },
  values: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:operator', 'update:values']);

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');

const currentLabel = computed(() => '');

const filterTypes = [
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS'),
    value: 'does_not_contain',
  },
];

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value
        .map(label => ({ label: label.title, value: label.title }))
        .filter(label => !props.values.includes(label.value))
    : []
);

const handleLabelSelect = value => {
  if (!value || props.values.includes(value)) return;

  emit('update:values', [...props.values, value]);
};

const removeLabel = label => {
  emit(
    'update:values',
    props.values.filter(value => value !== label)
  );
};
</script>

<template>
  <div class="mb-4">
    <span
      class="inline-flex flex-wrap items-center gap-1.5 text-sm text-n-slate-12"
    >
      {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
      <FilterSelect
        :model-value="operator"
        variant="faded"
        :options="filterTypes"
        class="inline-flex shrink-0"
        @update:model-value="value => emit('update:operator', value)"
      />
      {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}

      <NextButton
        v-for="label in values"
        :key="label"
        sm
        faded
        slate
        trailing-icon
        :label="label"
        icon="i-lucide-x"
        class="inline-flex shrink-0"
        @click="removeLabel(label)"
      />
      <FilterSelect
        :model-value="currentLabel"
        :options="labelOptions"
        :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')"
        hide-label
        variant="faded"
        class="inline-flex shrink-0"
        @update:model-value="handleLabelSelect"
      />
    </span>
  </div>
</template>
